<template>
  <div class="ui-note-component-wrapper">
    <div class="ui-note-conponent" @click.stop>
      <div class="popup-body">
        <div class="popup-icon" v-if="type" :class="type">
          <i class="fa-solid" :class="typeIcon" />
        </div>
        <h3 :id="titleId" class="popup-title" v-text="title" />
        <p class="popup-message" v-text="message" />
        <textarea
          class="ui-note-input"
          v-model="confirmInput"
          :placeholder="placeholder || ''"
          :aria-labelledby="titleId"
        />
      </div>
      <div class="popup-footer">
        <button
          type="button"
          class="confirm-popup-cancel-button"
          @click="sendEvent(false)"
          v-text="cancelButton"
        />
        <button
          type="button"
          class="confirm-popup-confirm-button"
          @click="sendEvent(confirmInput)"
          v-text="confirmButton"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { INote } from '@/types/Components/Popup'
  import { computed, ref, useId } from 'vue'

  const confirmInput = ref<string>('')
  const titleId = useId()
  const emit = defineEmits(['response'])
  const {
    type = 'warning',
    title,
    message,
    placeholder,
    cancelButton,
    confirmButton,
  } = defineProps<INote>()

  const typeIcon = computed((): string => {
    switch (type) {
      case 'success':
        return 'fa-circle-check'
      case 'danger':
        return 'fa-circle-xmark'
      case 'warning':
        return 'fa-circle-exclamation'
      case 'info':
        return 'fa-circle-info'
      default:
        return ''
    }
  })

  const sendEvent = (event: string | boolean = false) => {
    emit('response', event)
  }
</script>

<style lang="scss">
  .ui-note-component-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    .ui-note-conponent {
      width: 400px;
      height: auto;
      background-color: #fff;
      border-radius: 5px;
      padding: 1rem;
      opacity: 0;
      animation: fadeIn 0.25s ease-in-out forwards;
      @keyframes fadeIn {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }

      .popup-body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        .popup-icon {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 10px;
          font-size: 3rem;
          &.warning {
            color: $warning;
          }
          &.success {
            color: $success;
          }
          &.danger {
            color: $danger;
          }
          &.info {
            color: $info;
          }
        }
        .popup-title {
          font-size: 1.5rem;
          font-weight: 500;
          color: $primary-text-color;
          margin: 1rem 0;
          text-align: center;
        }
        .popup-message {
          font-size: 1rem;
          font-weight: 400;
          text-align: center;
          margin: 0;
        }
        .ui-note-input {
          resize: none;
          width: 100%;
          height: 150px;
          font-family: 'QuickSand', sans-serif;
          border: 1px solid $border-color;
          border-radius: 5px;
          padding: 0.5rem;
          margin-top: 1rem;
          &:focus {
            outline: none;
            border: 1px solid $warning;
          }
        }
      }
      .popup-footer {
        display: flex;
        justify-content: flex-end;
        margin-top: 2rem;
        .confirm-popup-cancel-button {
          background-color: $cancel;
          color: $primary-text-color;
          border: none;
          padding: calc(0.5em - 1px) 1em;

          border-radius: 5px;
          margin-right: 10px;
          cursor: pointer;
        }
        .confirm-popup-confirm-button {
          background-color: $success;
          color: $white;
          outline: none;
          border: none;
          padding: calc(0.5em - 1px) 1em;

          border-radius: 5px;
          cursor: pointer;
          transition: all 0.25s ease-in-out;
          &:disabled {
            background-color: $disabled;
            color: $white;
            cursor: not-allowed;
          }
        }
      }
    }
  }
</style>
